import saveAs from "file-saver";

type CordovaInAppBrowser = {
  open: (url: string, target: string) => unknown;
};

export function preOpenExternalDownloadTab(): Window | null {
  if (!shouldPreOpenExternalDownloadTab()) {
    return null;
  }

  try {
    // iOS Safari in iframe is more reliable when we keep a direct reference
    // to a tab opened synchronously from the user gesture.
    return globalThis.open("about:blank", "_blank");
  } catch {
    return null;
  }
}

export function openDownloadPopupIfNeeded(): Window | null {
  if (shouldUseExternalDownloadLink()) {
    return null;
  }

  if (!shouldUseMobileIframePopupStrategy()) {
    return null;
  }

  try {
    return globalThis.open("about:blank", "_blank");
  } catch {
    return null;
  }
}

export function saveBlobFile(
  blob: Blob,
  filename: string,
  popup: Window | null = null
): void {
  const fileUrl = URL.createObjectURL(blob);

  if (!popup && shouldUseExternalDownloadLink()) {
    const openedInCurrentContext = openInCurrentBrowserContext(fileUrl);

    if (openedInCurrentContext) {
      setTimeout(() => URL.revokeObjectURL(fileUrl), 60_000);
      return;
    }
  }

  if (popup) {
    try {
      const link = popup.document.createElement("a");
      link.href = fileUrl;
      link.download = filename;
      popup.document.body.appendChild(link);
      link.click();
      link.remove();
      popup.close();
    } catch {
      // Some embedded browsers block scripted clicks in popup documents.
      popup.location.href = fileUrl;
    }
  } else {
    try {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      saveAs(blob, filename);
    }
  }

  setTimeout(() => URL.revokeObjectURL(fileUrl), 60_000);
}

function shouldUseMobileIframePopupStrategy(): boolean {
  const userAgent = navigator.userAgent;
  const isIos =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (/Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(userAgent);
  const isWebView = isInAppWebView(userAgent);

  let inIframe = false;
  try {
    inIframe = globalThis.self !== globalThis.top;
  } catch {
    inIframe = true;
  }

  // In app WebViews, popups can crash or terminate the host app.
  if (isWebView) {
    return false;
  }

  return inIframe && (isIos || isAndroid);
}

export function isInAppWrapperWebView(): boolean {
  return isInAppWebView(navigator.userAgent);
}

export function shouldUseExternalDownloadLink(): boolean {
  return isInAppWrapperWebView() || isIosInsideIframe();
}

function shouldPreOpenExternalDownloadTab(): boolean {
  return isIosInsideIframe();
}

export function openInExternalBrowser(
  url: string,
  preOpenedTab: Window | null = null
): boolean {
  if (preOpenedTab) {
    try {
      preOpenedTab.location.href = url;
      return true;
    } catch {
      // Continue to other strategies.
    }
  }

  // In iOS iframes, avoid popup prompts and prefer direct navigation.
  if (shouldPreOpenExternalDownloadTab()) {
    return openInCurrentBrowserContext(url);
  }
  const cordovaWindow = globalThis as typeof globalThis & {
    cordova?: { InAppBrowser?: CordovaInAppBrowser };
  };

  try {
    const cordovaBrowser = cordovaWindow.cordova?.InAppBrowser;
    if (cordovaBrowser?.open) {
      cordovaBrowser.open(url, "_system");
      return true;
    }
  } catch {
    // Fallback below.
  }

  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    return true;
  } catch {
    // Last fallback below.
  }

  try {
    const win = globalThis.open(url, "_blank", "noopener,noreferrer");
    return Boolean(win);
  } catch {
    return false;
  }
}

export function openInCurrentBrowserContext(url: string): boolean {
  try {
    globalThis.top?.location.assign(url);
    return true;
  } catch {
    // Ignore and fallback to current frame.
  }

  try {
    globalThis.location.assign(url);
    return true;
  } catch {
    // Continue to anchor fallback.
  }

  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.target = "_self";
    anchor.rel = "noopener noreferrer";
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    return true;
  } catch {
    return false;
  }
}

function isInAppWebView(userAgent: string): boolean {
  const androidWebView = /\bwv\b|Version\/\d+\.\d+/.test(userAgent);
  const iosWebView =
    /iPhone|iPad|iPod/.test(userAgent) && !/Safari/.test(userAgent);

  return androidWebView || iosWebView;
}

function isIosInsideIframe(): boolean {
  const userAgent = navigator.userAgent;
  const isIosDevice =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (/Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1);

  if (!isIosDevice) {
    return false;
  }

  try {
    return globalThis.self !== globalThis.top;
  } catch {
    return true;
  }
}
